/**
 * Step4HeightDifferences Component
 *
 * Wizard Step 4: Define height differences in the garden.
 * Two sections (#623/#624):
 * - "Grundgefälle" — global slope plane (direction + total drop), works
 *   without a canvas and is therefore the full mobile experience
 * - "Höhenzonen" — polygonal plateau zones (desktop only, Fabric.js canvas)
 *
 * Design based on: docs/HEIGHT_DIFFERENCES_SPECIFICATION.md (zones) and
 * docs/plans/PLAN_TERRAIN_GEFAELLE_623.md §3.2 (baseSlope)
 *
 * Features:
 * - Base slope: direction presets/preview + drop slider (10–500 cm)
 * - Height zones: Polygonal areas at different elevations (-200cm to +200cm)
 * - Color-coded visualization (Orange/Yellow/Green/Blue)
 * - Click-to-add-vertex polygon drawing (like Step 3)
 * - Background image and garden outline overlay from previous steps
 * - Snap-to-grid support
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Canvas, Circle, Line, Text, FabricImage, Polygon } from 'fabric';
import { Mountain, Info, RotateCcw, Undo, Eye, EyeOff } from 'lucide-react';
import { useWizard } from '../../context/WizardContext';
import { useGrid } from '../../context/GridContext';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { useHasCoarsePointer } from '../../hooks/useHasCoarsePointer';
import { getEventCoords, applyTouchActionNone, attachPinchZoom } from '../../utils/canvasTouchUtils';
import { drawViewportGrid } from '../../utils/drawViewportGrid';
import {
  getHeightColor,
  createHeightZone,
  addHeightZoneVertex,
  removeLastVertex,
  isNearPoint,
  // calculateDistance, // unused
  // calculatePolygonArea, // unused
  calculatePolygonCentroid,
  formatHeightLabel,
  formatZoneLabel,
  isSlopeZone,
  SLOPE_COLOR,
} from '../Editor/tools/HeightZoneTool';
import { snapPointToZones } from '../Editor/tools/zoneSnapUtils';
import { updateZoneVertex } from '../Editor/tools/zoneVertexEditUtils';
import { isPointInPolygon, hasSelfIntersection } from '../Editor/tools/PolygonTool';
import BaseSlopeControls, { clampDropCm, DEFAULT_DROP_CM } from './inputs/BaseSlopeControls';
import SnapToggleButton from './SnapToggleButton';

/**
 * Legacy maxHeight (meters, old mobile slider) as the default drop for the
 * slope slider — the field itself is no longer written (#624).
 */
const defaultDropFromMaxHeight = (maxHeight) =>
  maxHeight > 0 ? clampDropCm(maxHeight * 100) : DEFAULT_DROP_CM;

/**
 * Auswählbare Höhenstufen in cm, hoch → tief sortiert (#687) — so wie die
 * Legende und der Tooltip im selben Panel lesen. Beide Höhen-Dropdowns
 * teilen sich diese Liste, damit die Reihenfolge nicht auseinanderläuft.
 */
const HEIGHT_LEVELS_CM = Array.from({ length: 41 }, (_, i) => 200 - i * 10);

/**
 * Step4MobileView — shown on mobile instead of the Fabric.js canvas.
 * The base-slope section is the complete mobile experience (#624); zone
 * drawing stays desktop-only.
 */
const Step4MobileView = ({ gardenData, updateHeightDifferences, nextStep, northAngle }) => {
  const baseSlope = gardenData.heightDifferences?.baseSlope ?? null;

  const handleSkip = () => {
    updateHeightDifferences({ baseSlope: null });
    nextStep();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Mountain size={20} className="text-gray-700" />
        <h2 className="text-xl font-bold text-gray-900">Höhenunterschiede</h2>
      </div>

      <p className="text-sm text-gray-600">
        Hier legst du fest, ob dein Garten insgesamt geneigt ist.
        Einzelne Höhenzonen wie Terrassen kannst du am Desktop einzeichnen.
      </p>

      <BaseSlopeControls
        baseSlope={baseSlope}
        onChange={(next) => updateHeightDifferences({ baseSlope: next })}
        northAngle={northAngle}
        outline={gardenData.outline}
        defaultDropCm={defaultDropFromMaxHeight(gardenData.heightDifferences?.maxHeight ?? 0)}
        idPrefix="base-slope-mobile"
      />

      <button
        onClick={handleSkip}
        className="text-sm text-gray-500 underline min-h-[44px]"
      >
        Überspringen — ebene Fläche angenommen
      </button>
    </div>
  );
};

const Step4HeightDifferences = ({ northAngle = null }) => {
  const { gardenData, updateHeightDifferences, nextStep } = useWizard();
  const { gridEnabled, snapEnabled } = useGrid();
  const { isMobile } = useBreakpoint();
  const isCoarsePointer = useHasCoarsePointer();

  const gridSize = 10; // 10px = 10cm

  // State
  const [heightDifferencesEnabled, setHeightDifferencesEnabled] = useState(
    gardenData.heightDifferences?.enabled || false
  );
  const [zones, setZones] = useState(gardenData.heightDifferences?.zones || []);
  const [currentHeight, setCurrentHeight] = useState(0); // Default height (ground level)
  // Zeichenwerkzeug (#625, Revision): 'plateau' = ebene Fläche mit Höhe,
  // 'slope' = Böschung (ohne Höhe, interpoliert im 3D aus den Nachbar-Ebenen)
  const [drawTool, setDrawTool] = useState('plateau');
  // Ausgewählte Zone (#5): einzeln editieren/löschen
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  // Vertex-Editing (#628): Snapshot-Stack für „Punkt-Verschieben rückgängig"
  const [moveHistory, setMoveHistory] = useState([]);
  const [vertices, setVertices] = useState([]);
  const [isClosed, setIsClosed] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [mousePosition, setMousePosition] = useState(null);
  const [ctrlPressed, setCtrlPressed] = useState(false);
  const [spacePressed, setSpacePressed] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const touchDrawPendingRef = useRef(false);
  const touchDrawPointRef = useRef(null);
  const [showBackgroundImage, setShowBackgroundImage] = useState(true);
  const [nearFirstVertex, setNearFirstVertex] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1); // eslint-disable-line no-unused-vars
  const [canvasReady, setCanvasReady] = useState(false);

  // Canvas refs
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const backgroundImageRef = useRef(null);
  const outlinePolygonRef = useRef(null);
  const vertexMarkersRef = useRef([]);
    const edgeLinesRef = useRef([]);
    const previewLineRef = useRef(null);
    const zonePolygonsRef = useRef([]);
    const zoneLabelsRef = useRef([]);
    // Vertex-Editing (#628): Drag-Handles der ausgewählten Zone + Live-Drag-Status
    const zoneVertexHandlesRef = useRef([]);
    const vertexDragRef = useRef(null);
    // Live-Snap-Einstellungen (Refs, damit ein Drag nicht durch Ctrl-Tastendruck
    // oder Snap-Toggle neu aufgesetzt/abgebrochen wird). Synchronisiert in einem
    // Effect (Refs dürfen nicht während des Renderns geschrieben werden).
    const snapSettingsRef = useRef({ snapEnabled: true, gridSize: 10, ctrlPressed: false });

    const safeRenderAll = useCallback((canvas) => {
      if (canvas && typeof canvas.renderAll === 'function') {
        canvas.renderAll();
      }
    }, []);

  /**
   * Initialize Fabric.js canvas
   * Important: Canvas should only be initialized when heightDifferencesEnabled is true
   * to ensure it's ready when the user wants to draw
   */
  useEffect(() => {
    // Only initialize canvas when height differences are enabled
    if (!heightDifferencesEnabled) {
      return;
    }

    if (!canvasRef.current) {
      return;
    }

    if (fabricCanvasRef.current) {
      setCanvasReady(true);
      return;
    }

    const canvas = new Canvas(canvasRef.current, {
      width: 960,
      height: 600,
      // No Fabric backgroundColor: the canvas stays transparent so the grid,
      // drawn on the `before:render` hook, is not painted over. The Gray-50
      // backdrop comes from the wrapper element's CSS.
      selection: false,
      defaultCursor: 'crosshair',
      hoverCursor: 'crosshair',
    });

    fabricCanvasRef.current = canvas;
    applyTouchActionNone(canvas);
    setCanvasReady(true);

    return () => {
      if (fabricCanvasRef.current?.dispose) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
        setCanvasReady(false);
      }
    };
  }, [heightDifferencesEnabled]);

  /**
   * Load background image if uploaded in Step 2
   */
  useEffect(() => {
    if (!fabricCanvasRef.current || !gardenData.backgroundImage?.dataUrl) return;

    const canvas = fabricCanvasRef.current;

    // Remove old background image if it exists
    if (backgroundImageRef.current) {
      canvas.remove(backgroundImageRef.current);
      backgroundImageRef.current = null;
    }

    FabricImage.fromURL(gardenData.backgroundImage.dataUrl, {
      crossOrigin: 'anonymous',
    }).then((fabricImage) => {
      // Calculate scale to fit canvas (with 20px padding)
      const maxWidth = 920;
      const maxHeight = 560;
      const scale = Math.min(maxWidth / fabricImage.width, maxHeight / fabricImage.height);

      // Use saved position and rotation from Step 2
      const savedPosition = gardenData.backgroundImage.position || { x: 20, y: 20 };
      const savedRotation = gardenData.backgroundImage.rotation || 0;

      fabricImage.set({
        scaleX: scale,
        scaleY: scale,
        left: savedPosition.x,
        top: savedPosition.y,
        angle: savedRotation,
        opacity: gardenData.backgroundImage.transparency / 100,
        selectable: false,
        evented: false,
        hasControls: false,
        hasBorders: false,
        originX: 'left',
        originY: 'top',
        visible: showBackgroundImage,
      });

      canvas.add(fabricImage);
      canvas.sendObjectToBack(fabricImage);
      backgroundImageRef.current = fabricImage;

      canvas.requestRenderAll();
    });

    return () => {
      if (backgroundImageRef.current) {
        canvas.remove(backgroundImageRef.current);
        backgroundImageRef.current = null;
      }
    };
  }, [gardenData.backgroundImage?.dataUrl, gardenData.backgroundImage?.position, gardenData.backgroundImage?.rotation, gardenData.backgroundImage?.transparency, showBackgroundImage]);

  /**
   * Toggle background image visibility
   */
  useEffect(() => {
    if (backgroundImageRef.current) {
      backgroundImageRef.current.set({
        visible: showBackgroundImage,
      });
      fabricCanvasRef.current?.requestRenderAll();
    }
  }, [showBackgroundImage]);

  /**
   * Update background image transparency
   */
  useEffect(() => {
    if (backgroundImageRef.current && gardenData.backgroundImage?.transparency !== undefined) {
      backgroundImageRef.current.set({
        opacity: gardenData.backgroundImage.transparency / 100,
      });
      fabricCanvasRef.current?.requestRenderAll();
    }
  }, [gardenData.backgroundImage]);

  /**
   * Display Garden Outline from Step 3 (read-only overlay)
   */
  useEffect(() => {
    if (!canvasReady || !fabricCanvasRef.current || !gardenData.outline?.vertices || !heightDifferencesEnabled) return;

    const canvas = fabricCanvasRef.current;

    // Remove old outline if it exists
    if (outlinePolygonRef.current) {
      canvas.remove(outlinePolygonRef.current);
      outlinePolygonRef.current = null;
    }

    const outline = new Polygon(gardenData.outline.vertices, {
      originX: 'left',
      originY: 'top',
      fill: 'transparent',
      stroke: '#10B981',
      strokeWidth: 2,
      strokeDashArray: [5, 5],
      selectable: false,
      evented: false,
      opacity: 0.5,
      data: { type: 'garden-outline' },
    });

    if (typeof canvas.add === 'function') {
      canvas.add(outline);
      outlinePolygonRef.current = outline;
    }

    if (typeof canvas.requestRenderAll === 'function') {
      canvas.requestRenderAll();
    }

    return () => {
      if (outlinePolygonRef.current && canvas) {
        canvas.remove(outlinePolygonRef.current);
        outlinePolygonRef.current = null;
      }
    };
  }, [canvasReady, heightDifferencesEnabled, gardenData.outline]);

  /**
   * Draw grid via the Fabric.js `before:render` hook. The grid is computed from
   * the live viewportTransform each frame, so it stays continuous and fills the
   * full visible surface at any zoom/pan (matching the 2D editor's GridOverlay).
   * The earlier static Line-object grid was drawn in world space (0..960/0..600)
   * and collapsed into the top-left corner when zooming out.
   */
  useEffect(() => {
    if (!canvasReady || !fabricCanvasRef.current || !heightDifferencesEnabled) return;

    const canvas = fabricCanvasRef.current;
    if (!gridEnabled) {
      if (typeof canvas.requestRenderAll === 'function') {
        canvas.requestRenderAll();
      }
      return;
    }

    const drawGrid = ({ ctx }) => {
      drawViewportGrid(ctx, canvas, { gridSize });
    };

    canvas.on('before:render', drawGrid);
    canvas.requestRenderAll();

    return () => {
      canvas.off('before:render', drawGrid);
      if (typeof canvas.requestRenderAll === 'function') {
        canvas.requestRenderAll();
      }
    };
  }, [canvasReady, heightDifferencesEnabled, gridEnabled, gridSize]);

  /**
   * Create vertex marker (circle) after placement
   */
  const createVertexMarker = useCallback((x, y, index) => {
    const isFirst = index === 0;
    // Larger hit target on touch so zone points are grabbable with a finger (#747).
    const radius = isFirst ? (isCoarsePointer ? 13 : 8) : (isCoarsePointer ? 11 : 6);
    const color = getHeightColor(currentHeight);

    const marker = new Circle({
      radius,
      fill: color,
      stroke: '#FFFFFF',
      strokeWidth: 2,
      left: x,
      top: y,
      originX: 'center',
      originY: 'center',
      selectable: false,
      hasControls: false,
      hasBorders: false,
      hoverCursor: 'pointer',
      data: {
        type: 'vertex-marker',
        vertexIndex: index,
      },
    });

    return marker;
  }, [currentHeight, isCoarsePointer]);

  /**
   * Render polygon preview (vertices and edges)
   */
  const renderPolygonPreview = useCallback(() => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;
    const color = getHeightColor(currentHeight);

    // Optimization: Disable auto-render while adding/removing multiple objects
    canvas.renderOnAddRemove = false;

    // Clear existing markers and edges
    vertexMarkersRef.current.forEach((marker) => canvas.remove(marker));
    edgeLinesRef.current.forEach((line) => canvas.remove(line));
    vertexMarkersRef.current = [];
    edgeLinesRef.current = [];

    // Draw edges (connecting lines)
    for (let i = 0; i < vertices.length - 1; i++) {
      const v1 = vertices[i];
      const v2 = vertices[i + 1];

      const line = new Line([v1.x, v1.y, v2.x, v2.y], {
        stroke: color,
        strokeWidth: 2,
        selectable: false,
        evented: false,
        data: { type: 'polygon-edge' },
      });
      canvas.add(line);
      edgeLinesRef.current.push(line);
    }

    // Draw vertex markers
    vertices.forEach((vertex, index) => {
      const marker = createVertexMarker(vertex.x, vertex.y, index);
      canvas.add(marker);
      vertexMarkersRef.current.push(marker);
    });

    // Bring outline to front so it stays visible above drawing elements
    if (outlinePolygonRef.current) {
      canvas.bringObjectToFront(outlinePolygonRef.current);
    }

    // Re-enable auto-render and render once
    canvas.renderOnAddRemove = true;
    if (typeof canvas.renderAll === 'function') {
      safeRenderAll(canvas);
    }
  }, [vertices, currentHeight, createVertexMarker, safeRenderAll]);

  /**
   * Update polygon preview whenever vertices change
   */
  useEffect(() => {
    if (isDrawing && vertices.length > 0) {
      renderPolygonPreview();
    }

    // Cleanup function
    return () => {
      if (!fabricCanvasRef.current) return;
      const canvas = fabricCanvasRef.current;

      vertexMarkersRef.current.forEach((marker) => canvas.remove(marker));
      edgeLinesRef.current.forEach((line) => canvas.remove(line));
      vertexMarkersRef.current = [];
      edgeLinesRef.current = [];
    };
  }, [isDrawing, vertices, renderPolygonPreview]);

  /**
   * Draw live preview line (from last vertex to mouse)
   */
  useEffect(() => {
    if (!fabricCanvasRef.current || !isDrawing || vertices.length === 0 || !mousePosition) return;

    const canvas = fabricCanvasRef.current;
    const color = getHeightColor(currentHeight);

    // Remove old preview line
    if (previewLineRef.current) {
      canvas.remove(previewLineRef.current);
    }

    const lastVertex = vertices[vertices.length - 1];

    // Apply snap-to-grid to preview position
    const snappedX = snapEnabled && !ctrlPressed
      ? Math.round(mousePosition.x / gridSize) * gridSize
      : mousePosition.x;
    const snappedY = snapEnabled && !ctrlPressed
      ? Math.round(mousePosition.y / gridSize) * gridSize
      : mousePosition.y;

    const previewLine = new Line([lastVertex.x, lastVertex.y, snappedX, snappedY], {
      stroke: color,
      strokeWidth: 2,
      strokeDashArray: [5, 5],
      selectable: false,
      evented: false,
      data: { type: 'preview-line' },
    });

    canvas.add(previewLine);
    previewLineRef.current = previewLine;

    if (typeof canvas.renderAll === 'function') {
      safeRenderAll(canvas);
    }

    return () => {
      if (previewLineRef.current) {
        canvas.remove(previewLineRef.current);
        previewLineRef.current = null;
      }
    };
  }, [mousePosition, vertices, isDrawing, snapEnabled, ctrlPressed, currentHeight, safeRenderAll]);

  /**
   * Render all saved zones on canvas
   */
  const renderZones = useCallback(() => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;

    // Clear old zone polygons and labels
    zonePolygonsRef.current.forEach((obj) => canvas.remove(obj));
    zoneLabelsRef.current.forEach((obj) => canvas.remove(obj));
    zonePolygonsRef.current = [];
    zoneLabelsRef.current = [];

    // Render each zone (Ebene = farbcodiert, Böschung = neutral + gestrichelt)
    zones.forEach((zone) => {
      const isSelected = zone.id === selectedZoneId;
      const polygon = createHeightZone(zone.vertices, zone.height, {
        id: zone.id,
        label: zone.label,
        kind: zone.kind,
        evented: true, // #5: anklickbar zum Auswählen
      });
      // Ausgewählte Zone hervorheben
      if (isSelected && typeof polygon.set === 'function') {
        polygon.set({ strokeWidth: 4, stroke: '#059669' });
      }

      canvas.add(polygon);
      zonePolygonsRef.current.push(polygon);

      // Label an der Fläche: Ebene = Höhe, Böschung = „Böschung"
      const centroid = calculatePolygonCentroid(zone.vertices);
      const label = new Text(formatZoneLabel(zone), {
        left: centroid.x,
        top: centroid.y,
        fontSize: 14,
        fontWeight: 'bold',
        fontFamily: 'Inter, sans-serif',
        fill: isSlopeZone(zone) ? '#374151' : zone.color,
        backgroundColor: '#FFFFFF',
        padding: 4,
        originX: 'center',
        originY: 'center',
        selectable: false,
        evented: false,
      });

      canvas.add(label);
      zoneLabelsRef.current.push(label);
    });

    // Bring outline to front so it stays visible above zones
    if (outlinePolygonRef.current) {
      canvas.bringObjectToFront(outlinePolygonRef.current);
    }

    if (typeof canvas.renderAll === 'function') {
      safeRenderAll(canvas);
    }
  }, [zones, selectedZoneId, safeRenderAll]);

  /**
   * Update rendered zones whenever zones array changes
   */
  useEffect(() => {
    renderZones();
  }, [renderZones]);

  /**
   * Live-update a rendered zone polygon from world-space vertices during a drag
   * (#628). Mirrors exactly what a fresh `new Polygon(vertices, {originX:'left',
   * originY:'top'})` would compute (bbox/pathOffset/left/top), so the shape stays
   * consistent when a vertex moves outside the original bounding box.
   */
  // Keep the live-drag snap settings ref in sync with grid state (#628).
  useEffect(() => {
    snapSettingsRef.current.snapEnabled = snapEnabled;
    snapSettingsRef.current.gridSize = gridSize;
  }, [snapEnabled, gridSize]);

  const setPolygonWorldVertices = useCallback((polygon, worldVertices) => {
    if (!polygon || typeof polygon.set !== 'function' || worldVertices.length === 0) return;
    const xs = worldVertices.map((v) => v.x);
    const ys = worldVertices.map((v) => v.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const maxX = Math.max(...xs);
    const maxY = Math.max(...ys);
    polygon.set({
      points: worldVertices.map((v) => ({ x: v.x, y: v.y })),
      left: minX,
      top: minY,
      width: maxX - minX,
      height: maxY - minY,
      pathOffset: { x: (minX + maxX) / 2, y: (minY + maxY) / 2 },
    });
    if (typeof polygon.setCoords === 'function') polygon.setCoords();
  }, []);

  /**
   * Vertex-Editing (#628): Draggable handles for the currently selected zone.
   * Only active when a zone is selected and we're not mid-drawing. Vertices live
   * in world/scene coordinates in state, so a handle sits directly at vertex.x/y.
   * Dragging snaps (zone-snap → grid → Ctrl override), rejects self-intersections,
   * live-updates the polygon, and commits to `zones` on drag end (with an undo
   * snapshot). Betrifft Ebenen UND Böschungen gleichermaßen.
   */
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || typeof canvas.add !== 'function' || !canvasReady || !heightDifferencesEnabled) {
      return;
    }
    if (isDrawing || !selectedZoneId) return;

    const zone = zones.find((z) => z.id === selectedZoneId);
    if (!zone || !Array.isArray(zone.vertices) || zone.vertices.length < 3) return;

    // The rendered polygon for this zone (populated by renderZones, which runs first)
    const polygon = zonePolygonsRef.current.find((p) => p?.data?.id === selectedZoneId) || null;

    const makeHandle = (vertex, index) => {
      const handle = new Circle({
        radius: isCoarsePointer ? 11 : 6, // #747: bigger touch hit target for zone-edit handles
        fill: '#10B981',
        stroke: '#FFFFFF',
        strokeWidth: 2,
        left: vertex.x,
        top: vertex.y,
        originX: 'center',
        originY: 'center',
        selectable: true,
        evented: true,
        hasControls: false,
        hasBorders: false,
        hoverCursor: 'grab',
        moveCursor: 'grabbing',
        data: { type: 'zone-vertex-handle', vertexIndex: index },
      });

      handle.on('moving', () => {
        const { snapEnabled: se, gridSize: gs, ctrlPressed: ctrl } = snapSettingsRef.current;
        const raw = { x: handle.left, y: handle.top };

        // Snap: an andere Zonen (Vorrang, eigene ausgenommen) → Raster → Ctrl hebt beides auf
        const zoneSnap = ctrl ? { snapped: false } : snapPointToZones(raw, zones, gs, selectedZoneId);
        const gridSnapped = se && !ctrl
          ? { x: Math.round(raw.x / gs) * gs, y: Math.round(raw.y / gs) * gs }
          : { x: raw.x, y: raw.y };
        const eff = zoneSnap.snapped ? { x: zoneSnap.x, y: zoneSnap.y } : gridSnapped;

        const candidate = zone.vertices.map((v, i) =>
          i === index ? { x: eff.x, y: eff.y } : { x: v.x, y: v.y }
        );

        // Selbstüberschneidung: Handle auf letzte gültige Position zurücksetzen
        if (hasSelfIntersection(candidate)) {
          const last = vertexDragRef.current?.vertices?.[index] ?? zone.vertices[index];
          handle.set({ left: last.x, top: last.y });
          handle.setCoords();
          return;
        }

        handle.set({ left: eff.x, top: eff.y });
        handle.setCoords();
        vertexDragRef.current = { index, vertices: candidate };
        setPolygonWorldVertices(polygon, candidate);
        canvas.requestRenderAll();
      });

      handle.on('modified', () => {
        const finalVertices = vertexDragRef.current?.vertices;
        vertexDragRef.current = null;
        // Kein echter Drag (nur Klick) ⇒ nichts committen
        if (!finalVertices) return;
        // Undo-Snapshot (pre-move) sichern, dann Zonen-State aktualisieren
        setMoveHistory((prev) => [...prev.slice(-19), zones]);
        setZones((prev) => updateZoneVertex(prev, selectedZoneId, index, finalVertices[index]));
      });

      return handle;
    };

    canvas.renderOnAddRemove = false;
    const handles = zone.vertices.map((v, i) => makeHandle(v, i));
    handles.forEach((h) => canvas.add(h));
    canvas.renderOnAddRemove = true;
    zoneVertexHandlesRef.current = handles;
    safeRenderAll(canvas);

    return () => {
      handles.forEach((h) => canvas.remove(h));
      zoneVertexHandlesRef.current = [];
      vertexDragRef.current = null;
      safeRenderAll(canvas);
    };
  }, [
    canvasReady,
    heightDifferencesEnabled,
    isDrawing,
    selectedZoneId,
    zones,
    setPolygonWorldVertices,
    safeRenderAll,
    isCoarsePointer,
  ]);

  /**
   * Handle canvas mouse move (update preview)
   */
  const handleCanvasMouseMove = useCallback(
    (e) => {
      if (!fabricCanvasRef.current || !isDrawing) return;

      const canvas = fabricCanvasRef.current;
      const pointer = canvas.getScenePoint(e.e);

      setMousePosition(pointer);
      touchDrawPointRef.current = pointer; // #747: last position for commit-on-lift

      // Check if near first vertex for auto-close hint
      if (vertices.length >= 3) {
        const near = isNearPoint(pointer, vertices[0], 10);
        setNearFirstVertex(near);
      } else {
        setNearFirstVertex(false);
      }
    },
    [vertices, isDrawing]
  );

  /**
   * Close current zone and save it
   */
  const handleCloseZone = useCallback(() => {
    if (vertices.length < 3) return;

    const isSlope = drawTool === 'slope';
    const newZone = {
      id: crypto?.randomUUID?.() || `zone-${Date.now()}`,
      vertices,
      kind: isSlope ? 'slope' : 'plateau',
      // Ebene: Höhe + Farbcode. Böschung: keine Höhe, neutrale Farbe.
      ...(isSlope
        ? { color: SLOPE_COLOR }
        : { height: currentHeight, color: getHeightColor(currentHeight) }),
      isClosed: true,
      createdAt: new Date().toISOString(),
    };

    const updatedZones = [...zones, newZone];
    setZones(updatedZones);

    // Reset for next zone
    setVertices([]);
    setIsClosed(false);
    setIsDrawing(false);
    setMousePosition(null);
    setNearFirstVertex(false);

    // Clear markers and edges
    if (fabricCanvasRef.current) {
      const canvas = fabricCanvasRef.current;
      vertexMarkersRef.current.forEach((marker) => canvas.remove(marker));
      edgeLinesRef.current.forEach((line) => canvas.remove(line));
      if (previewLineRef.current) {
        canvas.remove(previewLineRef.current);
        previewLineRef.current = null;
      }
      vertexMarkersRef.current = [];
      edgeLinesRef.current = [];
      safeRenderAll(canvas);
    }
  }, [vertices, currentHeight, drawTool, zones, safeRenderAll]);

  /**
   * Handle canvas click (add vertex or close polygon)
   */
  const handleCanvasClick = useCallback(
    (e, explicitPointer) => {
      if (!fabricCanvasRef.current || !heightDifferencesEnabled) return;

      const canvas = fabricCanvasRef.current;
      // On touch, placement commits on lift with the last finger position passed in
      // explicitly; on mouse it's the click point. (#747)
      const pointer = explicitPointer ?? canvas.getScenePoint(e.e);

      // #628: Klick/Drag auf einen Vertex-Handle gehört dem Fabric-Drag —
      // niemals als Zeichen-/Auswahl-Klick behandeln.
      if (e.target?.data?.type === 'zone-vertex-handle') return;

      // #5: Klick auf eine bestehende Zone (außerhalb des Zeichnens) wählt sie aus
      if (!isDrawing && vertices.length === 0 && e.target?.data?.shapeType === 'height-zone') {
        setSelectedZoneId(e.target.data.id ?? null);
        return;
      }

      // Check if clicking first vertex to close
      if (e.target?.data?.type === 'vertex-marker' &&
          e.target.data.vertexIndex === 0 &&
          vertices.length >= 3) {
        handleCloseZone();
        return;
      }

      // Andere Vertex-Marker blockieren
      if (e.target?.data?.type === 'vertex-marker' && e.target.data.vertexIndex !== 0) {
        return;
      }

      // Start drawing if not already (verwirft eine bestehende Auswahl)
      if (!isDrawing && !isClosed) {
        setIsDrawing(true);
        setSelectedZoneId(null);
      }

      // #2: an bestehende Zonen-Punkte/-Kanten snappen (Vorrang vor Raster),
      // außer Ctrl. Sonst Raster-Snap. Danach kein Doppel-Snap mehr.
      const zoneSnap = ctrlPressed
        ? { snapped: false }
        : snapPointToZones(pointer, zones, gridSize);
      const gridSnapped = snapEnabled && !ctrlPressed
        ? { x: Math.round(pointer.x / gridSize) * gridSize, y: Math.round(pointer.y / gridSize) * gridSize }
        : { x: pointer.x, y: pointer.y };
      const effective = zoneSnap.snapped
        ? { x: zoneSnap.x, y: zoneSnap.y }
        : gridSnapped;

      // Check if point is inside garden outline (if outline exists)
      if (gardenData.outline?.vertices && gardenData.outline.vertices.length >= 3) {
        if (!isPointInPolygon(effective, gardenData.outline.vertices)) {
          console.log('Point outside garden outline - click ignored');
          return;
        }
      }

      // Vertex hinzufügen (bereits gesnappt ⇒ addVertex nicht erneut snappen lassen)
      const result = addHeightZoneVertex(vertices, effective, ctrlPressed, gridSize, false);

      if (result.shouldClose) {
        handleCloseZone();
        return;
      }

      if (!result.isDuplicate) {
        setVertices(result.vertices);
      }
    },
    [vertices, isDrawing, isClosed, ctrlPressed, snapEnabled, heightDifferencesEnabled, handleCloseZone, gardenData.outline, gridSize, zones]
  );

  /**
   * Handle panning with spacebar and single-finger touch
   */
  const handlePanMouseDown = useCallback((opt) => {
    if (spacePressed) {
      setIsPanning(true);
      const { x, y } = getEventCoords(opt.e);
      lastPosRef.current = { x, y };
    }
  }, [spacePressed]);

  const handlePanMouseMove = useCallback((opt) => {
    if (!isPanning) return;
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    const { x, y } = getEventCoords(opt.e);
    if (x == null || Number.isNaN(x) || y == null || Number.isNaN(y)) return;
    const dx = x - lastPosRef.current.x;
    const dy = y - lastPosRef.current.y;
    canvas.relativePan({ x: dx, y: dy });
    canvas.requestRenderAll();
    lastPosRef.current = { x, y };
  }, [isPanning]);

  const handlePanMouseUp = useCallback(() => {
    if (isPanning) {
      setIsPanning(false);
    }
  }, [isPanning]);

  /**
   * Attach mouse event handlers
   */
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || typeof canvas.on !== 'function' || typeof canvas.off !== 'function') return;

    const handleMouseDown = (opt) => {
      const isTouch =
        opt.e.pointerType === 'touch' ||
        (typeof TouchEvent !== 'undefined' && opt.e instanceof TouchEvent);
      // Single-finger touch pan: only in edit mode (isClosed), not during drawing
      if (isTouch && !opt.target && isClosed) {
        setIsPanning(true);
        const { x, y } = getEventCoords(opt.e);
        lastPosRef.current = { x, y };
        return;
      }
      // Touch drawing: defer point placement to lift (mouse:up) so it can be dragged
      // into position first — touch has no hover to preview before a tap (#747).
      if (isTouch && !opt.target && !isClosed && !spacePressed) {
        const canvas = fabricCanvasRef.current;
        if (canvas) {
          const pointer = canvas.getScenePoint(opt.e);
          touchDrawPointRef.current = pointer;
          setMousePosition(pointer);
          touchDrawPendingRef.current = true;
        }
        return;
      }
      if (spacePressed) {
        handlePanMouseDown(opt);
      } else {
        handleCanvasClick(opt);
      }
    };

    const handleMouseMove = (opt) => {
      if (isPanning) {
        handlePanMouseMove(opt);
      } else {
        handleCanvasMouseMove(opt);
      }
    };

    const handleMouseUp = (opt) => {
      if (isPanning) {
        handlePanMouseUp(opt);
        return;
      }
      // Commit a deferred touch point at the lift position (#747).
      if (touchDrawPendingRef.current) {
        touchDrawPendingRef.current = false;
        if (touchDrawPointRef.current) {
          handleCanvasClick(opt, touchDrawPointRef.current);
        }
      }
    };

    canvas.on('mouse:down', handleMouseDown);
    canvas.on('mouse:move', handleMouseMove);
    canvas.on('mouse:up', handleMouseUp);

    return () => {
      canvas.off('mouse:down', handleMouseDown);
      canvas.off('mouse:move', handleMouseMove);
      canvas.off('mouse:up', handleMouseUp);
    };
  }, [handleCanvasClick, handleCanvasMouseMove, handlePanMouseDown, handlePanMouseMove, handlePanMouseUp, spacePressed, isPanning, isClosed]);

  /**
   * Handle "Clear and Restart" button
   */
  const handleClearAndRestart = useCallback(() => {
    setVertices([]);
    setIsClosed(false);
    setIsDrawing(false);
    setMousePosition(null);
    setNearFirstVertex(false);

    // Clear canvas markers and lines
    if (fabricCanvasRef.current) {
      const canvas = fabricCanvasRef.current;
      vertexMarkersRef.current.forEach((marker) => canvas.remove(marker));
      edgeLinesRef.current.forEach((line) => canvas.remove(line));
      if (previewLineRef.current) {
        canvas.remove(previewLineRef.current);
        previewLineRef.current = null;
      }
      vertexMarkersRef.current = [];
      edgeLinesRef.current = [];
      safeRenderAll(canvas);
    }
  }, [safeRenderAll]);

  /**
   * Handle keyboard events
   */
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check if user is typing in an input field
      const isTyping = ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName) ||
                       document.activeElement?.contentEditable === 'true';
      if (isTyping) return;

      if (e.key === 'Control') {
        setCtrlPressed(true);
        snapSettingsRef.current.ctrlPressed = true;
      }

      if (e.key === ' ') {
        e.preventDefault();
        setSpacePressed(true);
      }

      // Enter key to close polygon (if >= 3 vertices)
      if (e.key === 'Enter' && vertices.length >= 3 && isDrawing) {
        handleCloseZone();
      }

      // Escape key to cancel current drawing
      if (e.key === 'Escape' && isDrawing) {
        handleClearAndRestart();
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'Control') {
        setCtrlPressed(false);
        snapSettingsRef.current.ctrlPressed = false;
      }

      if (e.key === ' ') {
        setSpacePressed(false);
        setIsPanning(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [vertices, isDrawing, handleCloseZone, handleClearAndRestart]);

  /**
   * Handle "Undo Last Point" button
   */
  const handleUndoLastPoint = () => {
    if (vertices.length === 0) return;
    const newVertices = removeLastVertex(vertices);
    setVertices(newVertices);
  };

  /**
   * Handle "Clear All Zones" button
   */
  const handleClearAllZones = () => {
    setZones([]);
    setSelectedZoneId(null);
    setMoveHistory([]);
    handleClearAndRestart();
  };

  /**
   * Vertex-Editing (#628): letzten Vertex-Move rückgängig machen (Snapshot-Stack).
   * Ersatz für ein echtes Undo/Redo, das der Wizard-Canvas nicht hat.
   */
  const handleUndoVertexMove = () => {
    if (moveHistory.length === 0) return;
    const snapshot = moveHistory[moveHistory.length - 1];
    setMoveHistory(moveHistory.slice(0, -1));
    setZones(snapshot);
  };

  /**
   * Selected-zone editing (#5): change a plateau's height or delete a single zone.
   */
  const selectedZone = zones.find((z) => z.id === selectedZoneId) || null;

  const updateSelectedZoneHeight = (newHeight) => {
    setZones((prev) =>
      prev.map((z) =>
        z.id === selectedZoneId
          ? { ...z, height: newHeight, color: getHeightColor(newHeight) }
          : z
      )
    );
  };

  const deleteSelectedZone = () => {
    setZones((prev) => prev.filter((z) => z.id !== selectedZoneId));
    setSelectedZoneId(null);
    setMoveHistory([]);
  };

  /**
   * Two-finger pinch-to-zoom on touch devices (#746). Fabric dropped its
   * `touch:gesture` event after v5 — pinch is wired onto native touchmove.
   * Deps include heightDifferencesEnabled so it re-attaches after canvas re-init.
   */
  useEffect(() => {
    return attachPinchZoom(fabricCanvasRef.current, { onZoom: setZoomLevel });
  }, [canvasReady, heightDifferencesEnabled]);

  /**
   * Cancel an active pan when a second finger is placed.
   * Deps include heightDifferencesEnabled so the listener re-registers after canvas re-init.
   */
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    const upper = canvas?.upperCanvasEl;
    if (!upper) return;
    const onTouchStart = (e) => {
      if ((e.touches?.length ?? 0) >= 2) {
        if (isPanning) setIsPanning(false);
        if (touchDrawPendingRef.current) {
          touchDrawPendingRef.current = false; // #747: cancel a deferred point on pinch
          touchDrawPointRef.current = null;
          setMousePosition(null); // clear the dangling preview edge
        }
      }
    };
    upper.addEventListener('touchstart', onTouchStart, { passive: true });
    return () => upper.removeEventListener('touchstart', onTouchStart);
  }, [isPanning, canvasReady, heightDifferencesEnabled]);

  /**
   * Zoom functions
   */
  const handleZoomIn = () => {
    if (!fabricCanvasRef.current) return;
    const canvas = fabricCanvasRef.current;
    let zoom = canvas.getZoom() * 1.1;
    if (zoom > 5) zoom = 5;
    canvas.setZoom(zoom);
    setZoomLevel(zoom);
    canvas.requestRenderAll();
  };

  const handleZoomOut = () => {
    if (!fabricCanvasRef.current) return;
    const canvas = fabricCanvasRef.current;
    let zoom = canvas.getZoom() * 0.9;
    if (zoom < 0.1) zoom = 0.1;
    canvas.setZoom(zoom);
    setZoomLevel(zoom);
    canvas.requestRenderAll();
  };

  const handleZoomReset = () => {
    if (!fabricCanvasRef.current) return;
    const canvas = fabricCanvasRef.current;
    canvas.setZoom(1);
    canvas.viewportTransform = [1, 0, 0, 1, 0, 0];
    setZoomLevel(1);
    canvas.requestRenderAll();
  };

  /**
   * Update WizardContext when heightDifferences state changes
   */
  useEffect(() => {
    updateHeightDifferences({
      enabled: heightDifferencesEnabled,
      zones,
    });
  }, [heightDifferencesEnabled, zones, updateHeightDifferences]);

  /**
   * Load state from WizardContext (mount + on external data changes, e.g. draft
   * load). Adjusted during render via previous-value tracking instead of a
   * setState-in-effect: when the values originate locally they round-trip with the
   * same reference (reducer spreads ...payload), so setZones/setEnabled bail out —
   * no loop. Only a genuinely new reference (external change) re-syncs local state.
   */
  const nextHdEnabled = gardenData.heightDifferences?.enabled;
  const nextHdZones = gardenData.heightDifferences?.zones;
  const [prevHd, setPrevHd] = useState({ enabled: nextHdEnabled, zones: nextHdZones });
  if (prevHd.enabled !== nextHdEnabled || prevHd.zones !== nextHdZones) {
    setPrevHd({ enabled: nextHdEnabled, zones: nextHdZones });
    if (nextHdZones) {
      setZones(nextHdZones);
    }
    if (nextHdEnabled !== undefined) {
      setHeightDifferencesEnabled(nextHdEnabled);
    }
  }

  // Mobile/tablet: simplified slider view instead of Fabric.js canvas.
  // Show slider when: phone/small screen (isMobile) OR coarse pointer + viewport < 1024 px.
  const showSliderFallback = isMobile || (isCoarsePointer && window.innerWidth < 1024);
  if (showSliderFallback) {
    return <Step4MobileView
      gardenData={gardenData}
      updateHeightDifferences={updateHeightDifferences}
      nextStep={nextStep}
      northAngle={northAngle}
    />;
  }

  return (
    <div className="w-full space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Mountain size={20} className="text-gray-700" />
        <h2 className="text-xl font-bold text-gray-900">Höhenunterschiede</h2>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-600">
        Beschreibe hier Höhenunterschiede in deinem Garten: ein Gefälle über das
        ganze Grundstück und einzelne Zonen wie Terrassen, Senken oder erhöhte Beete.
        Beides ist optional und kann übersprungen werden.
      </p>

      {/* Section 1: Grundgefälle (#624) */}
      <BaseSlopeControls
        baseSlope={gardenData.heightDifferences?.baseSlope ?? null}
        onChange={(next) => updateHeightDifferences({ baseSlope: next })}
        northAngle={northAngle}
        outline={gardenData.outline}
        defaultDropCm={defaultDropFromMaxHeight(gardenData.heightDifferences?.maxHeight ?? 0)}
      />

      {/* Section 2: Höhenzonen — Toggle Question */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-3">
        <p className="text-base font-bold text-gray-900">
          Hat dein Garten einzelne Höhenzonen, etwa Terrassen oder Senken?
        </p>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="heightDifferences"
              checked={!heightDifferencesEnabled}
              onChange={() => {
                setHeightDifferencesEnabled(false);
                setZones([]);
                setVertices([]);
                setIsClosed(false);
                setIsDrawing(false);
              }}
              className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
            />
            <span className="text-sm font-medium text-gray-700">Nein</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="heightDifferences"
              checked={heightDifferencesEnabled}
              onChange={() => setHeightDifferencesEnabled(true)}
              className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
            />
            <span className="text-sm font-medium text-gray-700">Ja</span>
          </label>
        </div>
      </div>

      {/* 2-Column Layout: Canvas (Left) + Controls (Right) */}
      {heightDifferencesEnabled && (
        <div className="flex gap-4">
          {/* LEFT COLUMN: Canvas */}
          <div className="flex-shrink-0 space-y-3">
            {/* Info Badge */}
            <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Info size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-800">
                {isDrawing
                  ? `${drawTool === 'slope' ? 'Böschung' : 'Ebene'} zeichnen: klicke Eckpunkte, mind. 3.`
                  : zones.length === 0
                  ? 'Wähle rechts ein Werkzeug und klicke auf die Karte, um eine Fläche zu zeichnen. Eine bestehende Fläche antippen wählt sie aus.'
                  : `${zones.length} Fläche${zones.length === 1 ? '' : 'n'} gezeichnet. Weiter zeichnen oder eine Fläche zum Bearbeiten antippen.`}
              </p>
            </div>

            {/* Canvas with Zoom Controls */}
            <div className="relative border-2 border-dashed border-gray-300 rounded-lg overflow-hidden" style={{ width: '960px', height: '600px', backgroundColor: '#F9FAFB' }}>
              <canvas ref={canvasRef} />

            {/* Zoom Controls - Lower Right (matching Step3 design) */}
            <div
              style={{
                position: 'absolute',
                bottom: '16px',
                right: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                backgroundColor: 'white',
                padding: '8px',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                zIndex: 10,
              }}
            >
              <button
                onClick={handleZoomIn}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#F3F4F6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                }}
                style={{
                  width: '32px',
                  height: '32px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  transition: 'all 0.2s',
                }}
                aria-label="Zoom in"
              >
                +
              </button>
              <button
                onClick={handleZoomOut}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#F3F4F6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                }}
                style={{
                  width: '32px',
                  height: '32px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  transition: 'all 0.2s',
                }}
                aria-label="Zoom out"
              >
                −
              </button>
              <button
                onClick={handleZoomReset}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#F3F4F6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                }}
                style={{
                  width: '32px',
                  height: '32px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  fontSize: '10px',
                  transition: 'all 0.2s',
                }}
                aria-label="Reset zoom"
              >
                1:1
              </button>
            </div>
          </div>

            {/* Keyboard Shortcuts */}
            <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg border border-gray-200">
              <p className="font-semibold mb-2">Tastenkombinationen:</p>
              <ul className="space-y-1">
                <li>• Strg: Raster temporär deaktivieren</li>
                <li>• Leertaste: Canvas verschieben</li>
                <li>• Enter: Zone schließen (ab 3 Punkten)</li>
              </ul>
            </div>
          </div>

          {/* RIGHT COLUMN: Controls Sidebar */}
          <div className="space-y-3">
            {/* Werkzeug: Ebene | Böschung (#625, Revision) */}
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-semibold text-gray-700">Werkzeug:</span>
                <div className="relative group">
                  <Info size={14} className="text-gray-400 cursor-help" />
                  <div className="absolute left-0 top-6 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <p className="font-semibold mb-1">Ebene und Böschung</p>
                    <p>Zeichne die <strong>ebenen Flächen</strong> (Terrassen, Beete)
                      mit einer Höhe. Eine <strong>Böschung</strong> dazwischen
                      verbindet zwei Ebenen sanft — ihre Höhe und Neigung ergeben
                      sich automatisch, sichtbar in der 3D-Ansicht.</p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Zeichenwerkzeug">
                <button
                  type="button"
                  onClick={() => setDrawTool('plateau')}
                  aria-pressed={drawTool === 'plateau'}
                  disabled={isDrawing}
                  className={`min-h-[44px] px-2 text-sm rounded-md border transition-colors ${
                    drawTool === 'plateau'
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-100'
                  } disabled:opacity-50`}
                  data-testid="draw-tool-plateau"
                >
                  Ebene
                </button>
                <button
                  type="button"
                  onClick={() => setDrawTool('slope')}
                  aria-pressed={drawTool === 'slope'}
                  disabled={isDrawing}
                  className={`min-h-[44px] px-2 text-sm rounded-md border transition-colors ${
                    drawTool === 'slope'
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-100'
                  } disabled:opacity-50`}
                  data-testid="draw-tool-slope"
                >
                  Böschung
                </button>
              </div>
            </div>

            {/* Height Selector — nur für Ebenen */}
            {drawTool === 'plateau' && (
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <label htmlFor="step4-height-select" className="text-sm font-semibold text-gray-700">Höhe der Ebene:</label>
                  <div className="relative group">
                    <Info size={14} className="text-gray-400 cursor-help" />
                    <div className="absolute left-0 top-6 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      <p className="font-semibold mb-1">💡 Höhen-System</p>
                      <p>Alle Höhen sind relativ zu deinem gewählten 0cm-Bezugspunkt (z.B. Hausniveau, Terrasse).</p>
                      <p className="mt-2">
                        <span className="text-emerald-400">Positive Werte</span> = höher,<br />
                        <span className="text-blue-400">Negative Werte</span> = tiefer
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    id="step4-height-select"
                    value={currentHeight}
                    onChange={(e) => setCurrentHeight(Number(e.target.value))}
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    disabled={isClosed && vertices.length > 0}
                  >
                    {HEIGHT_LEVELS_CM.map((h) => (
                      <option key={h} value={h}>
                        {h > 0 ? '+' : ''}{h} cm
                      </option>
                    ))}
                  </select>
                  <div
                    className="w-8 h-8 rounded border-2 border-gray-300 flex-shrink-0"
                    style={{ backgroundColor: getHeightColor(currentHeight) }}
                    title={`Farbe für ${currentHeight > 0 ? '+' : ''}${currentHeight} cm`}
                  />
                </div>
              </div>
            )}

            {drawTool === 'slope' && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-xs text-gray-600">
                  Zeichne die Böschung zwischen zwei Ebenen. Höhe und Neigung
                  ergeben sich automatisch aus den angrenzenden Ebenen.
                </p>
              </div>
            )}

            {/* Ausgewählte Zone bearbeiten/löschen (#5) */}
            {selectedZone && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-2" data-testid="selected-zone-panel">
                <p className="text-xs font-bold text-emerald-900">
                  Ausgewählt: {isSlopeZone(selectedZone) ? 'Böschung' : 'Ebene'}
                </p>
                {!isSlopeZone(selectedZone) && (
                  <div className="flex items-center gap-2">
                    <label htmlFor="selected-zone-height" className="text-xs text-gray-700">Höhe:</label>
                    <select
                      id="selected-zone-height"
                      value={selectedZone.height ?? 0}
                      onChange={(e) => updateSelectedZoneHeight(Number(e.target.value))}
                      className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      data-testid="selected-zone-height"
                    >
                      {HEIGHT_LEVELS_CM.map((h) => (
                        <option key={h} value={h}>{h > 0 ? '+' : ''}{h} cm</option>
                      ))}
                    </select>
                  </div>
                )}
                <p className="text-xs text-emerald-800">
                  Ziehe die Eckpunkte, um die Fläche anzupassen.
                </p>
                {moveHistory.length > 0 && (
                  <button
                    onClick={handleUndoVertexMove}
                    className="w-full px-3 py-2 text-xs font-medium rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors flex items-center justify-center gap-1.5"
                    data-testid="undo-vertex-move"
                  >
                    <Undo size={14} />
                    <span>Punkt-Verschieben rückgängig</span>
                  </button>
                )}
                <button
                  onClick={deleteSelectedZone}
                  className="w-full px-3 py-2 text-xs font-medium rounded-md bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                  data-testid="delete-selected-zone"
                >
                  Diese Zone löschen
                </button>
              </div>
            )}

            {/* Height Legend */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs font-bold text-blue-900 mb-2">🎨 Höhen-Legende:</p>
              <div className="space-y-1.5 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded" style={{ background: '#FFA500' }} />
                  <span className="text-blue-800">Orange: +80 cm+</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded" style={{ background: '#FFFF00' }} />
                  <span className="text-blue-800">Gelb: +50-79 cm</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded" style={{ background: '#90EE90' }} />
                  <span className="text-blue-800">Grün: -10 bis +49</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded" style={{ background: '#87CEEB' }} />
                  <span className="text-blue-800">Blau: -30 cm−</span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded border border-dashed border-gray-500"
                    style={{ background: `${SLOPE_COLOR}33` }}
                  />
                  <span className="text-blue-800">Grau (gestrichelt): Böschung</span>
                </div>
              </div>
            </div>

            {/* Control Buttons */}
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 space-y-2">
              <p className="text-xs font-semibold text-gray-700 mb-2">Aktionen:</p>

              {/* Snap-to-grid toggle — touch-operable alternative to the Ctrl override (#752) */}
              <SnapToggleButton className="w-full justify-center" />

              {/* Toggle Background Image */}
              {gardenData.backgroundImage?.dataUrl && (
                <button
                  onClick={() => setShowBackgroundImage(!showBackgroundImage)}
                  className={`w-full px-3 py-2 text-xs font-medium rounded-md transition-colors flex items-center justify-center gap-1.5 ${
                    showBackgroundImage
                      ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {showBackgroundImage ? (
                    <>
                      <Eye size={14} />
                      <span>Bild sichtbar</span>
                    </>
                  ) : (
                    <>
                      <EyeOff size={14} />
                      <span>Bild ausblenden</span>
                    </>
                  )}
                </button>
              )}

              {/* Undo Last Point */}
              {isDrawing && vertices.length > 0 && !isClosed && (
                <button
                  onClick={handleUndoLastPoint}
                  className="w-full px-3 py-2 text-xs font-medium rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors flex items-center justify-center gap-1.5"
                >
                  <Undo size={14} />
                  <span>Letzter Punkt zurück</span>
                </button>
              )}

              {/* Clear All Zones */}
              {(isDrawing || zones.length > 0) && (
                <button
                  onClick={handleClearAllZones}
                  className="w-full px-3 py-2 text-xs font-medium rounded-md bg-red-100 text-red-700 hover:bg-red-200 transition-colors flex items-center justify-center gap-1.5"
                >
                  <RotateCcw size={14} />
                  <span>Alle löschen</span>
                </button>
              )}
            </div>

            {/* Status Display */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-gray-700 mb-2">Status:</p>

              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">Höhen-Zonen:</span>
                <span className="font-bold text-gray-900">{zones.length}</span>
              </div>

              {isDrawing && vertices.length > 0 && (
                <>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Aktuelle Punkte:</span>
                    <span className="font-bold text-emerald-600">{vertices.length}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Höhe:</span>
                    <span className="font-bold text-gray-900">
                      {formatHeightLabel(currentHeight)}
                    </span>
                  </div>
                </>
              )}

              {snapEnabled && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">Raster:</span>
                  <span className="text-gray-600">
                    {gridSize} cm {ctrlPressed && '(Aus)'}
                  </span>
                </div>
              )}

              {/* Proximity Hint */}
              {nearFirstVertex && isDrawing && vertices.length >= 3 && (
                <div className="text-xs text-emerald-600 font-bold bg-emerald-50 p-2 rounded mt-2">
                  💡 Ersten Punkt klicken zum Schließen
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step4HeightDifferences;
